import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { consentAppSelectors } from 'redux/slices/consent.slice';
import { Button, FormBuilder } from '@act/storybook';
import { Box, Divider, Typography } from '@mui/material';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ClientConsentTerm,
  ClientDetails,
  ClientDto,
  CreateClientRequest,
  CreateClientSchema,
  InternalPhoneNumberAdminViewDto,
  UpdateClientRequest,
  UpdateClientSchema,
} from '@act/shared/data-transfer-objects';
import {
  clientCreatedSuccess,
  clientUpdatedSuccess,
} from 'redux/slices/clientSlice';
import { internalPhoneNumbersSelectors } from 'redux/slices/internalPhoneNumberSlice';
import { useAppDispatch } from 'redux/store-hooks';
import { useToast } from 'react-components/app/ReusableToast';

import { teamsRequests } from 'redux/requests/teams.requests';
import { useApiCall } from 'redux/utils/api-hook';
import { userAdministratorRequests } from 'redux/requests/user-administrator.requests';
import { RecordContainer } from './styles';
import { EditActions } from '../../AdminTools.utils';
import useUnsavedChangesDialog from '@act/react-components/dialog-forms/hooks/use-unsaved-changes-dialog';
import { clientsRequests } from 'redux/requests/clients.requests';
import { createCustomerFormConfig } from './CustomerFormConfig';

export interface ClientCreate {
  id: number;
  name: string;
  displayName: string;
  avonMedicalCenterId?: string;
  feedbackNumberId?: string;
  internalPhoneNumberId?: string;
  callEngagementLengthRequirementInMinutes: number;
  reemaWebsite: string;
  terms: ClientConsentTerm[];
  smsReEngagementThreshold: number;
  createMode: boolean;
}

export const recordFields = [
  'name',
  'displayName',
  'findHelpApiKey',
  'avonMedicalCenterId',
  'feedbackNumberId',
  'internalPhoneNumberId',
  'reemaWebsite',
  'hasADTFeed',
];
export const configFields = [
  'smsReEngagementThreshold',
  'callEngagementLengthRequirementInMinutes',
  'terms',
  'teamIds',
];

interface Props {
  stopCreating: () => void;
  createMode: boolean;
  canEdit: boolean;
  customerDetail: ClientDto;
}

export const CreateEditCustomer = ({
  stopCreating,
  createMode,
  canEdit,
  customerDetail,
}: Props) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const [selectedTerms, _setSelectedTerms] = useState([]);
  const [_initialSelectedTerms, setInitialSelectedTerms] = useState<number[]>(
    []
  );
  const [_initialRecordValues, setInitialRecordValues] =
    useState<Partial<ClientDetails | null>>();
  const [_initialConfigsValues, setInitialConfigsValues] =
    useState<Partial<ClientDetails | null>>();
  const [recordEditMode, setRecordEditMode] = useState(false);
  const [configsEditMode, setConfigsEditMode] = useState(false);
  const allTerms = consentAppSelectors.useSelectAllConsentTerms();
  const [updatedClient, _updatedClientLoading, updateClient] = useApiCall(
    clientsRequests.updateClient
  );
  const [createdClient, _createdClientLoading, createClient] = useApiCall(
    clientsRequests.createClient
  );
  const schema = createMode ? CreateClientSchema : UpdateClientSchema;
  const { control, handleSubmit, formState, getValues } = useForm<
    CreateClientRequest | UpdateClientRequest
  >({
    resolver: zodResolver(schema),
    defaultValues: {
      name: customerDetail?.name ?? '',
      displayName: customerDetail?.displayName ?? '',
      avonMedicalCenterId: customerDetail?.avonMedicalCenterId ?? undefined,
      internalPhoneNumberId:
        customerDetail?.clientMarketNumbers.find(
          number => number.type === 'phoneNumber'
        )?.id ?? undefined,
      feedbackNumberId:
        customerDetail?.clientMarketNumbers.find(
          number => number.type === 'feedbackNumber'
        )?.id ?? undefined,
      callEngagementLengthRequirementInMinutes:
        customerDetail?.callEngagementLengthRequirementInMinutes ?? undefined,
      smsReEngagementThreshold:
        customerDetail?.smsReEngagementThreshold ?? undefined,
      reemaWebsite: customerDetail?.reemaWebsite ?? undefined,
      terms: customerDetail?.terms?.flatMap(term => term.id) ?? [],
      teamIds: customerDetail?.teamIds ?? [],
      hasADTFeed: customerDetail?.hasADTFeed ?? false,
    },
  });
  const handleEditToggle = (
    setEditMode: (value: boolean) => void,
    currentEditMode: boolean,
    section: 'configs' | 'customerRecord'
  ) => {
    if (!currentEditMode) {
      const values = getValues();
      const sectionFields =
        section === 'customerRecord' ? recordFields : configFields;

      const initialValues = sectionFields.reduce((acc, field) => {
        acc[field] = values[field];
        return acc;
      }, {} as Partial<ClientDetails>);

      if (section === 'customerRecord') {
        setInitialRecordValues(initialValues);
      } else {
        setInitialConfigsValues(initialValues);
        setInitialSelectedTerms([...selectedTerms]);
      }
    }
    setEditMode(!currentEditMode);
  };

  const defaultInternalNumbers =
    internalPhoneNumbersSelectors.useSelectAllDefaultInternalNumbers();

  const feedbackInternalNumbers =
    internalPhoneNumbersSelectors.useSelectAllFeedbackInternalNumbers();

  const [medicalCenters, _medicalCentersLoading, getAvonMedicalCenters] =
    useApiCall(userAdministratorRequests.getAvonMedicalCenters);

  const [teams, _teamsLoading, getTeams] = useApiCall(
    teamsRequests.getTeamsAsAdmin
  );

  useEffect(() => {
    if (!medicalCenters) {
      void getAvonMedicalCenters({ params: null });
    }
    if (!teams) {
      void getTeams({});
    }
  }, []);

  const teamOptions = useMemo(() => {
    return (
      teams?.map(item => ({
        label: item.name,
        value: item.id,
      })) ?? []
    );
  }, [teams]);

  const isRecordEditable = !createMode && (!recordEditMode || !canEdit);
  const isConfigEditable = !createMode && (!configsEditMode || !canEdit);

  const defaultphoneOptions = phoneOptions(defaultInternalNumbers);
  const feedbackPhoneOptions = phoneOptions(feedbackInternalNumbers);

  const medCenterOptions = medicalCenters?.map(item => ({
    key: item.id.toString(),
    label: `${item.name} (${item.id})`,
    value: item.id,
  }));

  const { customerRecordFormConfig, customerConfigFormConfig } =
    createCustomerFormConfig({
      createMode,
      isRecordEditable,
      isConfigEditable,
      medCenterOptions: medCenterOptions || [],
      defaultphoneOptions,
      feedbackPhoneOptions,
      allTerms,
      teamOptions,
    });

  const checkTermsChange = (customerDetail: ClientConsentTerm[]) => {
    const selectedTerms = getValues('terms');
    if (
      [...selectedTerms].sort().join(',') !==
      [...customerDetail?.flatMap(term => term.id)].sort().join(',')
    )
      return true;
  };
  const { dirtyFields } = formState;

  const onSubmit = async (
    data: CreateClientRequest,
    event?: 'customerRecord' | 'configs'
  ) => {
    if (!createMode) {
      const values = getValues();
      const allowedFields =
        event === 'customerRecord' ? recordFields : configFields;
      let changedFields = Object.keys(dirtyFields)
        .filter(field => allowedFields.includes(field))
        .reduce((acc, field) => {
          acc[field] = values[field];
          return acc;
        }, {});

      if (event === 'configs' && !!checkTermsChange(customerDetail.terms)) {
        changedFields = { ...changedFields, terms: values.terms };
      }

      updateClient({ params: { id: customerDetail.id }, body: changedFields });

      event === 'customerRecord'
        ? setRecordEditMode(false)
        : setConfigsEditMode(false);
    } else {
      createClient({ body: data });
    }
  };

  useEffect(() => {
    if (updatedClient) {
      dispatch(clientUpdatedSuccess(updatedClient));
      toast('Customer updated successfully', 'success');
    }
    if (createdClient) {
      dispatch(clientCreatedSuccess(createdClient));
      toast('Customer created successfully', 'success');
      stopCreating();
    }
  }, [updatedClient, createdClient]);

  const handleSaveWithConfirmation = async (
    section: 'customerRecord' | 'configs'
  ) => {
    await onSubmit(getValues(), section);
  };

  const handleCancelWithUnsavedCheck = (
    section: 'customerRecord' | 'configs'
  ) => {
    if (formState?.isDirty) {
      void handleUnsavedChanges(() => {
        if (section === 'customerRecord') {
          handleRecordCancel();
        } else {
          handleConfigsCancel();
        }
      });
    } else {
      if (section === 'customerRecord') {
        handleRecordCancel();
      } else {
        handleConfigsCancel();
      }
    }
  };

  const handleRecordCancel = () => {
    setRecordEditMode(false);
  };
  const handleConfigsCancel = () => {
    setConfigsEditMode(false);
  };

  return (
    <>
      <form onSubmit={handleSubmit(data => onSubmit(data, undefined))}>
        <RecordContainer>
          <Box
            sx={{
              display: 'flex',
              algnItems: 'center',
              justifyContent: 'space-between',
              alignSelf: 'stretch',
            }}
          >
            <Typography variant="headlineSmall">
              {createMode ? 'New Customer Record' : 'Customer Record'}{' '}
            </Typography>
            {!createMode && canEdit && (
              <EditActions
                isEditMode={recordEditMode}
                onClick={() =>
                  handleEditToggle(
                    setRecordEditMode,
                    recordEditMode,
                    'customerRecord'
                  )
                }
                handleCancel={() =>
                  handleCancelWithUnsavedCheck('customerRecord')
                }
                handleSave={() => handleSaveWithConfirmation('customerRecord')}
              />
            )}
          </Box>
          <Divider />

          <FormBuilder
            config={customerRecordFormConfig}
            control={control}
            formState={formState}
          />
        </RecordContainer>
        <RecordContainer>
          <Box
            sx={{
              display: 'flex',
              algnItems: 'center',
              justifyContent: 'space-between',
              alignSelf: 'stretch',
            }}
          >
            <Typography variant="headlineSmall">
              {createMode
                ? 'New Customer Configurations'
                : 'Customer Configurations'}
            </Typography>
            {!createMode && canEdit && (
              <EditActions
                isEditMode={configsEditMode}
                onClick={() =>
                  handleEditToggle(
                    setConfigsEditMode,
                    configsEditMode,
                    'configs'
                  )
                }
                handleCancel={() => handleCancelWithUnsavedCheck('configs')}
                handleSave={() => handleSaveWithConfirmation('configs')}
              />
            )}
          </Box>
          <Divider />

          <FormBuilder
            config={customerConfigFormConfig}
            control={control}
            formState={formState}
          />
        </RecordContainer>
        {createMode && (
          <Box
            sx={{
              display: 'flex',
              paddingBottom: 2,
              justifyContent: 'flex-start',
              gap: '16px',
            }}
          >
            <Button variant="outlined" onClick={stopCreating}>
              Cancel
            </Button>
            <Button type="submit">Save New Customer</Button>
          </Box>
        )}
      </form>
    </>
  );
};

const phoneOptions = (
  internalPhoneNumbers: InternalPhoneNumberAdminViewDto[]
) => {
  return internalPhoneNumbers.map(item => ({
    key: item.id.toString(),
    label: `${item?.description ?? item?.displayName} : ${item?.number}`,
    value: item?.id,
  }));
};
